import React, { useState, lazy, Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { Bed, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../images/10246984.jpeg';


const SERVER_URL = 'https://nuedesigns.com';


// ============ STYLED COMPONENTS ============


const ProjectPage = styled.div`
  min-height: 100vh;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: scroll;
  color: white;
  padding: 2rem;
  overflow-x: hidden;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  position: relative;
  z-index: 1;
`;


const Hero = styled.div`
  height: 20vh;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 3rem;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;

  @media (max-width: 768px) {
    height: 40vh;
    margin-bottom: 2rem;
  }
`;


const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.6);
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
`;


const HeroContent = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 37.5%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 2;
  width: 100%;
`;


const Title = styled.h1`
  font-family: "Raleway-Regular";
  color: #0a192f;
  font-size: clamp(2.5rem, 8vw, 2.75rem);
  margin: 0 0 1.75rem 0;
  font-weight: 700;
  letter-spacing: 0.03em;
`;


const Meta = styled.div`
  font-size: 1.1rem;
  color: #0a192f;
  opacity: 0.95;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;


const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: -5.5rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: -1.5rem;
  }
`;


const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
`;


const Description = styled.p`
  font-family: "Raleway-Regular";
  font-size: 1.2rem;
  line-height: 1.7;
  color: #0a192f;
  margin: 0;
  margin-top: 2.5rem;
  margin-bottom: -1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;


const Features = styled.div`
  padding: 2rem;
  border-radius: 16px;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  position: relative;
  z-index: 10;

  h3 {
    margin: 0 0 2.5rem 0;
    color: #0a192f;
    font-size: 1.3rem;
    font-family: "Raleway-Regular";
    font-weight: 800;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;

    h3 {
      font-size: 1.1rem;
    }
  }
`;


const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`;


const Feature = styled.li`
  background: #0a192fb9;
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid #e1ad21;
  color: #cec9ab;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  will-change: background, color;

  &:hover {
    background: #cec9ab;
    color: #0a192f;
    transform: translate3d(4px, 0, 0);
  }
`;


const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: -4.75rem;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
    margin-top: 0;
  }
`;


const ComingSoonBox = styled.div`
  background: rgba(10, 25, 47, 0.95);
  border-radius: 20px;
  padding: 3rem 2rem;
  position: relative;
  cursor: default;
  transition: border-color 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border: 2px solid #e1ad21;
  min-height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-align: center;
  gap: 1.5rem;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  will-change: border-color;

  @media (max-width: 768px) {
    min-height: 280px;
    padding: 2rem 1.5rem;
  }
`;


const ComingSoonIcon = styled.div`
  font-size: 3rem;
  color: #e1ad21;
  animation: pulse 2s ease-in-out infinite;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;

  @keyframes pulse {
    0%, 100% {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
    50% {
      transform: translate3d(0, 0, 0) scale(1.1);
      opacity: 0.8;
    }
  }
`;


const ComingSoonTitle = styled.h2`
  font-family: "Raleway-Regular";
  color: #e1ad21;
  font-size: 1.75rem;
  margin: 0;
  font-weight: 700;
`;


const ComingSoonText = styled.p`
  font-family: "Raleway-Regular";
  color: #cec9ab;
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  max-width: 280px;
`;


const DevelopmentNotice = styled.div`
  background: rgba(225, 173, 33, 0.1);
  border: 1px solid rgba(225, 173, 33, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-top: 2rem;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
`;


const NoticeIcon = styled.div`
  color: #e1ad21;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;


const NoticeText = styled.div`
  color: #cec9ab;
  font-size: 0.95rem;
  line-height: 1.6;

  strong {
    color: #e1ad21;
    display: block;
    margin-bottom: 0.5rem;
  }
`;


// ============ MAIN COMPONENT ============


function LuxuryThreeBedroom() {
  const navigate = useNavigate();

  return (
    <ProjectPage>
      {/* HERO SECTION */}
      <Hero>
        <HeroContent>
          <Title>Luxury Three-Bedroom</Title>
          <Meta>
            Residential • 3 Beds <Bed size={24} />
          </Meta>
        </HeroContent>
      </Hero>

      {/* MAIN CONTENT */}
      <Content>
        {/* LEFT COLUMN: TEXT & FEATURES */}
        <TextSection>
          <Description>
            An ultra-premium three-bedroom penthouse residence that epitomizes
            modern luxury and sophisticated design. This exclusive home features
            expansive living spaces, premium materials, and bespoke finishes.
            Designed for discerning clients who demand the finest in contemporary
            architecture and interior design excellence.
          </Description>

          <Features>
            <h3>Key Features</h3>
            <FeatureList>
              <Feature>✨ Luxury finishes</Feature>
              <Feature>✨ Spacious master suite</Feature>
              <Feature>✨ Premium appliances</Feature>
              <Feature>✨ Walk-in closets</Feature>
              <Feature>✨ Bespoke design</Feature>
              <Feature>✨ Smart home system</Feature>
            </FeatureList>
          </Features>
        </TextSection>

        {/* RIGHT COLUMN: COMING SOON */}
        <Sidebar>
          <ComingSoonBox>
            <ComingSoonIcon>
              <AlertCircle size={48} />
            </ComingSoonIcon>
            <ComingSoonTitle>Coming Soon</ComingSoonTitle>
            <ComingSoonText>
              This project is currently in development. Gallery, 3D models, and videos will be available shortly.
            </ComingSoonText>
          </ComingSoonBox>

          <DevelopmentNotice>
            <NoticeIcon>
              <AlertCircle size={20} />
            </NoticeIcon>
            <NoticeText>
              <strong>Project Status</strong>
              This project is currently in development. We're working on bringing you stunning visualizations, interactive 3D models, and immersive render videos.
            </NoticeText>
          </DevelopmentNotice>
        </Sidebar>
      </Content>
    </ProjectPage>
  );
}


export default LuxuryThreeBedroom;